import React, { Fragment } from 'react';
import { motion } from 'framer-motion';
import { BookOpenIcon } from 'lucide-react';
import { LatexRenderer } from '../../components/ui/LatexRenderer';
interface ReadingPassageProps {
  /** The passage text to display */
  passage: string;
  /** Instruction label, e.g. "Lees de tekst" */
  instruction?: string;
  /** Source attribution, e.g. "Fragment uit 'De Kleine Prins'" */
  source?: string;
  /** Optional label shown above the passage */
  label?: string;
  /** 'stimulus' = shown during question, 'support' = shown after answer */
  mode?: 'stimulus' | 'support';
  /** Show line numbers on the left */
  showLineNumbers?: boolean;
  /** Substring to highlight in the passage */
  highlightSpan?: string;
}
/**
 * ReadingPassage — Visual aid for reading comprehension questions.
 *
 * Renders a styled reading passage card with amber tint, book icon,
 * left border accent, and serif typography. Used as a stimulusAid
 * so the passage is clearly separated from the question text.
 *
 * Seed data format:
 * ```json
 * {
 *   "type": "readingPassage",
 *   "data": {
 *     "passage": "De kat zat op de mat...",
 *     "instruction": "Lees de tekst",
 *     "source": "Fragment uit 'De Kleine Prins'"
 *   }
 * }
 * ```
 */
export function ReadingPassage({
  passage,
  instruction,
  source,
  label,
  mode = 'stimulus',
  showLineNumbers = false,
  highlightSpan
}: ReadingPassageProps) {
  const isStimulus = mode === 'stimulus';
  // Simple highlight implementation: wrap the target span in a mark tag before LatexRenderer
  // Note: this is a basic string replace and might conflict with markdown syntax if they overlap
  const processedPassage = highlightSpan && passage.includes(highlightSpan) ? passage.replace(highlightSpan, `<mark class="bg-amber-200 text-amber-900 px-1 rounded">${highlightSpan}</mark>`) : passage;
  const lines = showLineNumbers ? processedPassage.split('\n') : [processedPassage];
  return <motion.div initial={{
    opacity: 0,
    y: 6
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.35,
    ease: [0.22, 1, 0.36, 1]
  }} className="w-full" data-id="element-13607" data-mp="17sbvkfu92obx">
      {/* Instruction header */}
      {(instruction || label) && <div className="flex items-center gap-2 mb-3" data-id="element-13608" data-mp="1mhrgvgb0i1k8">
          <BookOpenIcon className="w-4 h-4 text-amber-600" data-id="element-13609" data-mp="1qibeekzhz76w" />
          <span className="text-sm font-semibold text-amber-800 uppercase tracking-wide" data-id="element-13610" data-mp="1qhruiyqae8q8">
            {instruction || label}
          </span>
        </div>}

      {/* Passage card */}
      <div className="relative bg-amber-50/70 border-l-4 border-amber-400 rounded-r-xl overflow-hidden" data-id="element-13611" data-mp="20hsiwz30jf1a">
        {/* Decorative book icon */}
        <div className="absolute top-3 right-3 opacity-15 pointer-events-none" data-id="element-13612" data-mp="62g6xns4o7xf">
          <BookOpenIcon className="w-10 h-10 text-amber-600" data-id="element-13613" data-mp="64j7v2utjlgx" />
        </div>

        <div className={isStimulus ? 'p-4 sm:p-5' : 'p-5 sm:p-6 md:p-8'} data-id="element-13614" data-mp="b22vuuhbjcm2">
          <div className={`
              text-slate-700 leading-relaxed font-serif pr-10
              ${isStimulus ? 'text-base sm:text-lg' : 'text-lg sm:text-xl'}
            `} data-id="element-13615" data-mp="2oc2la44iz4bn">
            {showLineNumbers ? <div className="flex flex-col" data-id="element-13616" data-mp="2fjr1h13x2f4n">
                {lines.map((line, i) => <div key={i} className="flex gap-4 min-h-[1.5em]" data-id="element-13617" data-mp="2am8ydrckyu4k" data-mpi={i}>
                    <span className="text-xs text-amber-700/50 select-none w-6 text-right shrink-0 pt-1" data-id="element-13618" data-mp="379nchhuriezf">
                      {(i + 1) % 5 === 0 || i === 0 ? i + 1 : ''}
                    </span>
                    <div className="flex-1" data-id="element-13619" data-mp="2y8xlcrgg5cs1">
                      <LatexRenderer content={line || ' '} data-id="element-13620" data-mp="2yc36xwpv9wj0" />
                    </div>
                  </div>)}
              </div> : <div className="whitespace-pre-line" data-id="element-13621" data-mp="1mvmorvmxh1sg">
                <LatexRenderer content={processedPassage} data-id="element-13622" data-mp="1hvqoo7hcpui6" />
              </div>}
          </div>

          {/* Source attribution */}
          {source && <div className={`mt-3 pt-2 border-t border-amber-200/60 ${isStimulus ? 'text-xs' : 'text-sm'}`} data-id="element-13623" data-mp="35r55c3ah769i">
              <span className="text-amber-700/70 italic" data-id="element-13624" data-mp="2ws0bha9v013o">{source}</span>
            </div>}
        </div>
      </div>
    </motion.div>;
}